import { EPaymentPlan } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import { Caption, DataField, DisplayValue, FieldSectionTitle, SubsectionTitle } from '@if/mui'
import { Divider, Grid, Stack } from '@mui/material'
import {
  FormatPaymentPlanForSummary,
  getCoverageTotal,
  getMerchantFees,
  getSelectedPlan,
  getTotalObligation,
} from '@src/data/worksheet-selectors'
import { ComputeFundingResultDto } from '@src/types/ComputePersonalLoanFundingResultDto'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { TermAndPlanOption } from '@src/types/TermPlanOptionDto'
import { useTranslation } from 'react-i18next'
import WorksheetFundingSummaryCard from './WorksheetFundingSummaryCard'

interface FundingLine {
  label: string
  value: string
}

interface Props {
  creditApplication: CreditApplication
  amountRequested: number
  hasVariableInterest: boolean
  firstPaymentOn: string | null
  merchant: Merchant
  paymentPlanId: EPaymentPlan
  computedPersonalLoanFunding: ComputeFundingResultDto | null
  isComputing: boolean
  selectedTermAndPlanOption?: TermAndPlanOption
}

const PersonalLoanFundingSummary = ({
  creditApplication,
  amountRequested,
  hasVariableInterest,
  firstPaymentOn,
  merchant,
  paymentPlanId,
  computedPersonalLoanFunding,
  isComputing,
  selectedTermAndPlanOption,
}: Props) => {
  const { t } = useTranslation()
  const selectedPlan = getSelectedPlan(merchant, paymentPlanId)
  const totalObligation = getTotalObligation(amountRequested, computedPersonalLoanFunding)
  const insuranceTotal = getCoverageTotal(computedPersonalLoanFunding)
  const monthlyPayment = computedPersonalLoanFunding
    ? formatCurrency(computedPersonalLoanFunding.paymentForFrequency)
    : ''

  // Server-computed figures win over the option's own: they are what the contract will carry.
  const interestRateLabel = selectedTermAndPlanOption
    ? selectedTermAndPlanOption.promoDurationMonths
      ? `${selectedTermAndPlanOption.promoRate}%/${selectedTermAndPlanOption.promoDurationMonths} → ${selectedTermAndPlanOption.regularRate}%`
      : `${computedPersonalLoanFunding?.interestRate ?? selectedTermAndPlanOption.promoRate}%`
    : hasVariableInterest
      ? FormatPaymentPlanForSummary(selectedPlan)
      : `${creditApplication.finalCreditDecision.interestRate}%`

  const merchantFeeRateForDisplay =
    computedPersonalLoanFunding?.merchantFeeRate ??
    selectedTermAndPlanOption?.merchantFeeRate ??
    selectedPlan?.merchantFeeRate

  const fundingLines: FundingLine[] = computedPersonalLoanFunding
    ? [
        { label: t('worksheet.requestedAmount'), value: formatCurrency(amountRequested) },
        {
          label: `${t('worksheet.interest')} (${interestRateLabel})`,
          value: formatCurrency(computedPersonalLoanFunding.totalInterestAmount),
        },
        {
          label: `${t('worksheet.financeFee')} (${computedPersonalLoanFunding.lenderFeeRate}%)`,
          value: formatCurrency(computedPersonalLoanFunding.lenderFee),
        },
        ...(hasVariableInterest
          ? [
              {
                label: `${t('worksheet.merchantFee')} (${merchantFeeRateForDisplay}%)`,
                value: getMerchantFees(merchantFeeRateForDisplay, amountRequested),
              },
            ]
          : []),
        { label: t('worksheet.protectionPlan'), value: formatCurrency(insuranceTotal) },
        { label: t('worksheet.totalObligation'), value: formatCurrency(totalObligation) },
        { label: t('worksheet.annualPercentageRate'), value: `${computedPersonalLoanFunding.effectiveRate}%` },
      ]
    : []

  const paymentLines: FundingLine[] = computedPersonalLoanFunding
    ? [
        {
          label: t('worksheet.maxPmtAmount'),
          value: formatCurrency(creditApplication.finalCreditDecision.maxPmtAmount),
        },
        { label: t('worksheet.paymentFrequency'), value: t('common.monthly') },
        { label: t('worksheet.firstPaymentOn'), value: firstPaymentOn ? formatDate(firstPaymentOn) : '' },
        { label: t('worksheet.amountToBePaid'), value: formatCurrency(amountRequested ?? 0) },
      ]
    : []

  return (
    <WorksheetFundingSummaryCard
      title={t('worksheet.fundingSummary')}
      emptyMessage={isComputing ? t('common.loading') : t('worksheet.personalFundingInvalid')}
      isReady={Boolean(computedPersonalLoanFunding)}
    >
      <Stack spacing={2} divider={<Divider />}>
        <Stack spacing={0.5}>
          <FieldSectionTitle>{t('worksheet.monthlyPayment')}</FieldSectionTitle>
          <DisplayValue>{monthlyPayment}</DisplayValue>
        </Stack>
        <Grid container spacing={2}>
          {fundingLines.map((line) => (
            <Grid key={line.label} size={6}>
              <DataField label={line.label} value={line.value} />
            </Grid>
          ))}
        </Grid>
        <Stack spacing={2}>
          <SubsectionTitle>{t('worksheet.paymentTitle')}</SubsectionTitle>
          <Grid container spacing={2}>
            {paymentLines.map((line) => (
              <Grid key={line.label} size={6}>
                <DataField label={line.label} value={line.value} />
              </Grid>
            ))}
          </Grid>
        </Stack>
        <Caption>{t('worksheet.fundingSummaryFootnote')}</Caption>
      </Stack>
    </WorksheetFundingSummaryCard>
  )
}

export default PersonalLoanFundingSummary
